import { useQuery } from "@tanstack/react-query";

import { TENANT_MANAGEMENT_URL } from "constants/config";
import { DAY } from "constants/queryCacheTimes";
import { gatewayTenantHeader } from "store/backend/assets";
import { getAuthToken } from "utils/tokenUtils";

const getTenantManagementBaseUrl = () => {
  if (!TENANT_MANAGEMENT_URL) {
    throw new Error("Tenant management URL is not configured");
  }
  return TENANT_MANAGEMENT_URL;
};

const buildTenantManagementHeaders = () => ({
  ...gatewayTenantHeader(),
  accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

export const mapVerticalTenantsResponse = (response) =>
  (Array.isArray(response) ? response : [])
    .map((tenant) => {
      if (!tenant || typeof tenant !== "object") {
        return null;
      }

      const id = tenant.id ?? tenant.tenant_id;
      const name = tenant.name ?? tenant.display_name;

      if (id == null || !name) {
        return null;
      }

      return {
        id,
        name,
        tenantCode: tenant.tenant_code ?? tenant.tenantCode ?? null,
        countryId: tenant.country_id ?? tenant.countryId ?? null,
        currencyIds: tenant.currency_ids ?? tenant.currencyIds ?? [],
        timezoneIds: tenant.timezone_ids ?? tenant.timezoneIds ?? [],
        scaitsCampusId: tenant.scaits_campus_id ?? tenant.scaitsCampusId ?? null,
      };
    })
    .filter(Boolean);

export const listTenantsByVerticalId = async (verticalId) => {
  const response = await fetch(
    `${getTenantManagementBaseUrl()}/list-tenants-by-vertical-id/${verticalId}`,
    {
      method: "GET",
      headers: buildTenantManagementHeaders(),
    },
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      data?.message || "Failed to fetch tenants for the selected vertical.",
    );
  }

  return mapVerticalTenantsResponse(data);
};

export const useTenantsByVerticalId = (verticalId) =>
  useQuery({
    queryKey: ["tenantManagement", "tenants-by-vertical", verticalId],
    queryFn: () => listTenantsByVerticalId(verticalId),
    enabled: Boolean(TENANT_MANAGEMENT_URL) && Boolean(verticalId),
    staleTime: DAY,
  });

export const filterTenantsByCountry = (tenants = [], countryId) => {
  if (countryId == null || countryId === "") {
    return [];
  }
  return tenants.filter(
    (tenant) => Number(tenant.countryId) === Number(countryId),
  );
};

export const getTenantInfo = async (tenantId) => {
  const response = await fetch(
    `${getTenantManagementBaseUrl()}/get-tenant-info/${tenantId}`,
    {
      method: "GET",
      headers: buildTenantManagementHeaders(),
    },
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Failed to fetch tenant details.");
  }

  return data;
};

export const useTenantInfo = (tenantId) =>
  useQuery({
    queryKey: ["tenantManagement", "tenant-info", tenantId],
    queryFn: () => getTenantInfo(tenantId),
    enabled: Boolean(TENANT_MANAGEMENT_URL) && Boolean(tenantId),
    staleTime: DAY,
  });
